import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const [kind, setKind] = useState("pro"); // pro | credits
  const pollsRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    let active = true;
    const poll = async () => {
      if (!active) return;
      if (pollsRef.current >= MAX_POLLS) { setStatus("timeout"); return; }
      pollsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if ((data.lookup_key || "").startsWith("credits_")) setKind("credits");
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
        if (data.status === "failed" || data.payment_status === "failed") { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (active) setStatus("error");
      }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background noise-bg text-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center border border-white/10 rounded-2xl bg-white/[0.02] p-10" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-5" />
            <h1 className="font-display font-bold text-2xl">Confermiamo il pagamento...</h1>
            <p className="text-muted-foreground text-sm mt-2">Attendi qualche istante.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-success/10 border border-success/30 flex items-center justify-center text-success mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h1 className="font-display font-bold text-2xl" data-testid="payment-paid-title">
              {kind === "credits" ? "Crediti aggiunti!" : "Benvenuto in Angiò A.I. Pro!"}
            </h1>
            <p className="text-muted-foreground text-sm mt-2">
              {kind === "credits"
                ? "I tuoi crediti AI extra sono stati aggiunti al tuo account. Verranno usati quando finisci quelli del tuo piano."
                : "Il tuo abbonamento è attivo. Ora puoi creare siti illimitati e usare più generazioni AI."}
            </p>
            <Button onClick={() => navigate("/dashboard")} className="mt-7 bg-primary text-black hover:bg-[#33F3FF] font-semibold h-11 px-6" data-testid="go-dashboard-btn">
              Vai alla dashboard
            </Button>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-muted-foreground mx-auto mb-5">
              <XCircle className="w-9 h-9" />
            </div>
            <h1 className="font-display font-bold text-2xl">Verifica in corso</h1>
            <p className="text-muted-foreground text-sm mt-2">
              {status === "timeout"
                ? "Il pagamento sta impiegando più del previsto. Lo stato si aggiornerà a breve."
                : "Non siamo riusciti a confermare subito il pagamento. Se hai completato l'operazione, lo stato si aggiornerà a breve."}
            </p>
            <Button onClick={() => navigate("/dashboard")} className="mt-7 bg-white/10 text-white hover:bg-white/15 font-semibold h-11 px-6">
              Torna alla dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
